import { useNavigate, useParams } from 'react-router';
import { routes } from '../../constants/routes';
import { useQuery } from '@tanstack/react-query';
import { verifyMail } from '../../utils/apis';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import quibbleaiLogo from '../../assets/images/quibbleaiLogoDark.svg';

function VerifyMailSuccess() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { data, isSuccess, isLoading } = useQuery({
    queryKey: ['verify-mail', token || ''],
    queryFn: verifyMail,
    refetchOnWindowFocus: false,
  });

  return isLoading ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
      }}
    >
      <CircularProgress color="primary" size={48} />
    </Box>
  ) : isSuccess && data ? (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {/* Logo in top left corner */}
      <Box sx={{ p: 3, position: 'absolute', top: 0, left: 0 }}>
        <img src={quibbleaiLogo} alt="Quibble AI" style={{ width: '8rem' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '507px',
            height: '307px',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '12px',
            gap: '24px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CheckCircle
              size={64}
              weight="fill"
              color="#4CAF50"
              style={{
                background: '#e8f5e9',
                borderRadius: '50%',
                padding: '8px',
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'DM Sans',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '100%',
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            Email verified successfully
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontFamily: 'DM Sans',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '100%',
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            Your email has been verified successfully. Please login to the
            platform to create your AI voice assistant.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate(routes.login)}
            sx={{
              // py: 1.5,
              bgcolor: '#6C63FF',
              '&:hover': {
                bgcolor: '#5A52D9',
              },
              borderRadius: 1,
              textTransform: 'none',
            }}
          >
            Login to Platform
          </Button>
        </Paper>
      </Box>
    </Box>
  ) : (
    <VerifyMailError />
  );
}

export default VerifyMailSuccess;

export function VerifyMailError() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
        position: 'relative',
      }}
    >
      {/* Logo in top left corner */}
      <Box sx={{ p: 3, position: 'absolute', top: 0, left: 0 }}>
        <img src={quibbleaiLogo} alt="Quibble AI" style={{ width: '8rem' }} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '507px',
            height: '307px',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '12px',
            gap: '24px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <XCircle
              size={64}
              weight="fill"
              color="#F44336"
              style={{
                background: '#ffebee',
                borderRadius: '50%',
                padding: '8px',
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'DM Sans',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: '100%',
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            Email verification failed
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontFamily: 'DM Sans',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '100%',
              letterSpacing: '-0.01em',
              textAlign: 'center',
            }}
          >
            We couldn't verify your email. The verification link may have
            expired or is invalid. Please try signing up again.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate(routes.signup)}
            sx={{
              // py: 1.5,
              bgcolor: '#6C63FF',
              '&:hover': {
                bgcolor: '#5A52D9',
              },
              borderRadius: 1,
              textTransform: 'none',
            }}
          >
            Try Again
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}
